import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, LogOut } from "lucide-react";

const SUPPORT_EMAIL = (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) || "support@lysara.io";

function validateSlug(v: string): string | null {
  const s = v.toLowerCase().trim();
  if (s.length < 3 || s.length > 30) return "Company ID must be 3-30 characters long";
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(s)) {
    return "Only lowercase letters, numbers, and hyphens allowed";
  }
  if (["admin", "api", "www", "app", "mail", "ftp", "localhost", "lysara"].includes(s)) {
    return "This Company ID is reserved";
  }
  return null;
}

export function AccountSetupScreen() {
  const { user } = useAuth();
  const { refreshTenantData } = useTenant();

  const [slug, setSlug] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    const slugErr = validateSlug(slug);
    if (slugErr) {
      toast.error(slugErr);
      return;
    }
    if (!businessName.trim()) {
      toast.error("Business name is required");
      return;
    }
    if (!legalName.trim()) {
      toast.error("Company legal name is required (for tax)");
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("complete_pending_onboarding", {
      _requested_slug: slug.toLowerCase().trim(),
      _business_name: businessName.trim(),
      _legal_name: legalName.trim(),
      _contact_phone: phone.trim() || null,
    });
    setBusy(false);
    if (error) {
      const msg = error.message || "Could not complete setup";
      if (msg.includes("already taken") || msg.includes("23505")) {
        toast.error("That Company ID is taken. Please choose another.");
      } else if (msg.includes("active membership")) {
        toast.error("Your account already has an active organization.");
        await refreshTenantData();
      } else {
        toast.error(msg);
      }
      return;
    }
    toast.success("Setup submitted. Your account is now awaiting activation.");
    await refreshTenantData();
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut({ scope: "local" });
    window.location.href = "/auth";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Complete your account setup</CardTitle>
          <CardDescription>
            Signed in as <strong>{user?.email}</strong>. Your account isn't linked to an
            organization yet. Enter your company details to submit an activation request.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="slug">Company ID</Label>
            <Input
              id="slug"
              placeholder="e.g. acme"
              value={slug}
              onChange={(e) =>
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
              }
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              3-30 characters. Lowercase letters, numbers, and hyphens only.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="bname">Business name</Label>
            <Input
              id="bname"
              placeholder="Acme Logistics"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lname">Company legal name (for tax)</Label>
            <Input
              id="lname"
              placeholder="Acme Logistics Pte Ltd"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This is the name that will appear on invoices and receipts.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Contact phone (optional)</Label>
            <Input
              id="phone"
              placeholder="+65 …"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <Button onClick={handleSubmit} disabled={busy} className="w-full">
            {busy ? "Submitting…" : "Submit for activation"}
          </Button>
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                "Account setup help",
              )}&body=${encodeURIComponent(`Email: ${user?.email ?? ""}`)}`}
              className="hover:underline"
            >
              Need help? {SUPPORT_EMAIL}
            </a>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-1 hover:underline"
            >
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AccountSetupScreen;
